import React, { memo, useCallback, useMemo, useState } from 'react'
import { Auth } from '@aws-amplify/auth'
import Button from '@material-ui/core/Button'
import clsx from 'clsx'
import FormControl from '@material-ui/core/FormControl'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import SettingsPanel from '../../components/settings/SettingsPanel'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { makeStyles } from '@material-ui/core/styles'
import '../../components/settings/password/password.scss'
import Popup from '../../components/Popup'
import zxcvbn from 'zxcvbn'
import LOG from '../../logging/Logger'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  margin: {
    margin: theme.spacing(1)
  },
  withoutLabel: {
    marginTop: theme.spacing(3)
  },
  textField: {
    width: '25ch'
  },
  formHint: {
    fontSize: '0.6rem',
    lineHeight: 1.4,
    margin: '-5px auto 5px',
    color: '#999',

    error: {
      color: '#c00',
      fontSize: '0.8rem'
    }
  }
}))

const SettingsAccountProfile = memo(() => {
  LOG.debug('Render: SettingsAccountProfile')

  const classes = useStyles()
  const [values, setValues] = useState({
    amount: '',
    oldPassword: { value: '' },
    password: { value: '', strength: 0 },
    passwordConfirm: { value: '', strength: 0 },
    weight: '',
    weightRange: '',
    showPassword: false,
    showChangeMsg: false,
    errorMsg: '',
    msgSev: 'warning'
  })

  const handleChange = useCallback(
    prop => event => {
      LOG.debug('prop: ', prop)
      setValues({
        ...values,
        [prop]: {
          value: event.target.value,
          strength: zxcvbn(event.target.value).score
        }
      })
    },
    [values]
  )

  const handleClickShowPassword = useCallback(() => {
    setValues({ ...values, showPassword: !values.showPassword })
  }, [values])

  const handleMouseDownPassword = useCallback(event => {
    event.preventDefault()
  }, [])

  const handleErrorClose = useCallback(
    (event, reason) => {
      if (reason === 'clickaway') {
        return
      }

      setValues({ ...values, showChangeMsg: false })
    },
    [values]
  )

  const validateForm = useCallback(() => {
    return (
      values.oldPassword.value.length > 6 &&
      values.password.value.length > 6 &&
      values.password.value === values.passwordConfirm.value
    )
  }, [
    values.oldPassword.value.length,
    values.password.value,
    values.passwordConfirm.value
  ])

  const handleChangePassword = useCallback(
    async event => {
      event.preventDefault()

      let changeMsg
      let msgSev
      try {
        const user = await Auth.currentAuthenticatedUser()
        await Auth.changePassword(
          user,
          values.oldPassword.value,
          values.password.value
        )
        msgSev = 'success'
        changeMsg = 'Password successfully changed'
      } catch (err) {
        switch (err.code) {
          case 'NotAuthorizedException':
            changeMsg = 'The old password you entered was incorrect'
            break
          case 'InvalidParameterException':
            changeMsg = 'Your new password must be at least 6 characters'
            break
          case 'LimitExceededException':
            changeMsg =
              'You got your old password wrong too many times, please try later'
            break
          default:
            changeMsg = 'An unknown error occurred, please try again'
        }
        msgSev = 'warning'
      }
      setValues({ ...values, changeMsg, showChangeMsg: true, msgSev })
    },
    [values]
  )

  const passwordLength = useMemo(() => values.password.value.length, [
    values.password.value.length
  ])
  const passwordConfirmLength = useMemo(
    () => values.passwordConfirm.value.length,
    [values.passwordConfirm.value.length]
  )

  const strengthClass = useMemo(
    () =>
      ['strength-meter mt-2', passwordLength > 0 ? 'visible' : 'invisible']
        .join(' ')
        .trim(),
    [passwordLength]
  )

  const strengthConfirmClass = useMemo(
    () =>
      [
        'strength-meter mt-2',
        passwordConfirmLength > 0 ? 'visible' : 'invisible'
      ]
        .join(' ')
        .trim(),
    [passwordConfirmLength]
  )

  return (
    <SettingsPanel title='Password'>
      <span className={clsx('d-block', classes.formHint)}>
        To conform with our Strong Password policy, you are required to use a
        sufficiently strong password. Password must be more than 7 characters.
      </span>
      <br />
      <FormControl className={clsx(classes.margin, classes.textField)}>
        <InputLabel htmlFor='old-password'>Old Password</InputLabel>
        <Input
          id='old-password'
          type={values.showPassword ? 'text' : 'password'}
          value={values.oldPassword.value}
          onChange={handleChange('oldPassword')}
          endAdornment={
            <InputAdornment position='end'>
              <IconButton
                aria-label='toggle password visibility'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {values.showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
      <br/>
      <FormControl className={clsx(classes.margin, classes.textField)}>
        <InputLabel htmlFor='new-password'>Password</InputLabel>
        <Input
          id='new-password'
          type={values.showPassword ? 'text' : 'password'}
          value={values.password.value}
          onChange={handleChange('password')}
          endAdornment={
            <InputAdornment position='end'>
              <IconButton
                aria-label='toggle password visibility'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {values.showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
        <div className={strengthClass}>
          <div
            className='strength-meter-fill'
            data-strength={values.password.strength}
          ></div>
        </div>
      </FormControl>
      <br/>
      <FormControl className={clsx(classes.margin, classes.textField)}>
        <InputLabel htmlFor='new-password-confir'>Confirm Password</InputLabel>
        <Input
          id='new-password-confirm'
          type={values.showPassword ? 'text' : 'password'}
          value={values.passwordConfirm.value}
          onChange={handleChange('passwordConfirm')}
          endAdornment={
            <InputAdornment position='end'>
              <IconButton
                aria-label='toggle password visibility'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {values.showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
        />
        <div className={strengthConfirmClass}>
          <div
            className='strength-meter-fill'
            data-strength={values.passwordConfirm.strength}
          ></div>
        </div>
      </FormControl>
      <br/>
      <Button
        variant='contained'
        color='primary'
        onClick={handleChangePassword}
        disabled={!validateForm()}
      >
        Change Password
      </Button>
      <Popup
        open={values.showChangeMsg}
        message={values.changeMsg}
        onClose={handleErrorClose}
        variant={values.msgSev}
        autoHideDurationOverride={6000}
      />
    </SettingsPanel>
  )
})

export default SettingsAccountProfile
